import { useEffect, useState } from "react";
import { addDoc, collection, deleteDoc, doc, getDocs, query } from "firebase/firestore";
import { Navigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import "../Styles/events.css";
import { ADMIN_EMAIL, auth, db, firebaseReady } from "../firebase";

const createEmptyForm = () => ({
  title: "",
  date: "",
  time: "",
  endTime: "",
  location: "",
  image: "",
  ticketsUrl: "",
  isFree: false,
});

const formatEventDate = (dateValue) => {
  if (!dateValue) return "Date TBD";

  const parsedDate = new Date(`${dateValue}T12:00:00`);

  if (Number.isNaN(parsedDate.getTime())) {
    return dateValue;
  }

  return new Intl.DateTimeFormat("en-US", {
    weekday: "short",
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(parsedDate);
};

const formatEventTime = (timeValue) => {
  if (!timeValue) return "";

  const [hours, minutes] = timeValue.split(":").map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return timeValue;
  }

  const date = new Date();
  date.setHours(hours, minutes, 0, 0);

  return new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

export const dedupeEvents = (eventList) => {
  if (!Array.isArray(eventList)) {
    return [];
  }

  const seen = new Set();

  return eventList.filter((event) => {
    const key = event?.id ? `id:${event.id}` : `title:${event?.title ?? ""}|date:${event?.date ?? ""}`;

    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
};

const normalizeEvent = (eventDoc) => {
  const data = eventDoc.data();

  return {
    id: eventDoc.id,
    title: data.title || "Untitled event",
    date: data.date || "",
    time: data.time || "",
    endTime: data.endTime || "",
    location: data.location || "TBD",
    image:
      data.image ||
      "https://images.unsplash.com/photo-1516280440614-37939bbacd81?auto=format&fit=crop&w=1200&q=80",
    ticketsUrl: data.ticketsUrl || "",
    isFree: Boolean(data.isFree),
  };
};

const sortEventsByDate = (eventList) =>
  [...eventList].sort((a, b) => {
    if (!a.date && !b.date) return 0;
    if (!a.date) return 1;
    if (!b.date) return -1;
    return `${a.date}T${a.time || "00:00"}`.localeCompare(`${b.date}T${b.time || "00:00"}`);
  });

const fetchEvents = async () => {
  if (!db) {
    return [];
  }

  try {
    const eventsQuery = query(collection(db, "events"));
    const snapshot = await getDocs(eventsQuery);

    if (!snapshot.empty) {
      return sortEventsByDate(dedupeEvents(snapshot.docs.map(normalizeEvent)));
    }

    return [];
  } catch (error) {
    console.error("Unable to load events from Firestore:", error);
    return [];
  }
};

const Events = ({ adminMode = false }) => {
  const [events, setEvents] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(Boolean(adminMode && db && auth));
  const [formData, setFormData] = useState(createEmptyForm());

  useEffect(() => {
    if (!auth) {
      setUser(null);
      return undefined;
    }

    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadEvents = async () => {
      const nextEvents = await fetchEvents();

      if (isMounted) {
        setEvents(nextEvents);
        setLoading(false);
      }
    };

    if (!db) {
      setLoading(false);
      return () => {
        isMounted = false;
      };
    }

    if (adminMode) {
      if (!auth) {
        setLoading(false);
        return () => {
          isMounted = false;
        };
      }

      if (!auth.currentUser) {
        setLoading(false);
        return () => {
          isMounted = false;
        };
      }
    }

    loadEvents();

    return () => {
      isMounted = false;
    };
  }, [adminMode]);

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;

    setFormData((previousForm) => ({
      ...previousForm,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const isAdmin = Boolean(user && ADMIN_EMAIL && user.email?.trim().toLowerCase() === ADMIN_EMAIL);
  const showAdminControls = adminMode || isAdmin;

  const handleDeleteEvent = async (eventId) => {
    if (!eventId || !db || !showAdminControls) {
      return;
    }

    const confirmed = window.confirm("Are you sure you want to delete this event?");
    if (!confirmed) {
      return;
    }

    try {
      await deleteDoc(doc(db, "events", eventId));
      setEvents((currentEvents) => currentEvents.filter((event) => event.id !== eventId));
    } catch (error) {
      console.error("Failed to delete event from Firestore:", error);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formData.title.trim() || !formData.date) {
      return;
    }

    const newEvent = {
      id: Date.now(),
      title: formData.title.trim(),
      date: formData.date,
      time: formData.time || "",
      endTime: formData.endTime || "",
      location: formData.location.trim() || "TBD",
      image:
        formData.image ||
        "https://images.unsplash.com/photo-1516280440614-37939bbacd81?auto=format&fit=crop&w=1200&q=80",
      ticketsUrl: formData.ticketsUrl.trim(),
      isFree: formData.isFree,
    };

    if (db && auth?.currentUser) {
      try {
        await addDoc(collection(db, "events"), {
          ...newEvent,
        });

        const refreshedEvents = await fetchEvents();
        setEvents(refreshedEvents);
        setFormData(createEmptyForm());
        return;
      } catch (error) {
        console.error("Failed to save event to Firestore:", error);
      }
    }

    setFormData(createEmptyForm());
  };

  if (loading) {
    return (
      <section className="events-page">
        <div className="events-header">
          <p className="events-kicker">Upcoming Events</p>
          <h1>Loading events...</h1>
        </div>
      </section>
    );
  }

  return (
    <section className="events-page">
      <div className="events-header">
        <p className="events-kicker">Upcoming Events</p>
        <h1>Where I’m spinning next</h1>
      </div>

      <div className="events-layout">
        {adminMode ? (
          <form className="event-form" onSubmit={handleSubmit}>
            <h2>Add an event</h2>

            <label>
              Event title
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Summer rooftop set"
                required
              />
            </label>

            <label>
              Date
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleInputChange}
                required
              />
            </label>

            <label>
              Time
              <input
                type="time"
                name="time"
                value={formData.time}
                onChange={handleInputChange}
              />
            </label>

            <label>
              End time (optional)
              <input
                type="time"
                name="endTime"
                value={formData.endTime}
                onChange={handleInputChange}
              />
            </label>

            <label>
              Location
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                placeholder="Chicago, IL"
              />
            </label>

            <label>
              Event image URL
              <input
                type="url"
                name="image"
                value={formData.image}
                onChange={handleInputChange}
                placeholder="https://example.com/event-poster.jpg"
              />
            </label>


            <label>
              Ticket link
              <input
                type="url"
                name="ticketsUrl"
                value={formData.ticketsUrl}
                onChange={handleInputChange}
                placeholder="https://tickets.example.com/event"
              />
            </label>

            <label className="checkbox-row">
              <input
                type="checkbox"
                name="isFree"
                checked={formData.isFree}
                onChange={handleInputChange}
              />
              Mark as free event
            </label>

            <button type="submit" className="event-submit-button">
              Add event
            </button>
          </form>
        ) : null}

        <div className="events-grid" aria-label="Upcoming DJ events">
          {events.map((event) => (
            <article className="event-card" key={event.id}>
              <img src={event.image} alt={event.title} className="event-image" />

              <div className="event-content">
                <div className="event-topline">
                  <span className="event-date">{formatEventDate(event.date)}</span>
                  <span className={`event-badge ${event.isFree ? "free" : "paid"}`}>
                    {event.isFree ? "Free" : "Ticketed"}
                  </span>
                </div>
                {event.time ? (
                  <p className="event-time">
                    {event.endTime
                      ? `${formatEventTime(event.time)} - ${formatEventTime(event.endTime)}`
                      : formatEventTime(event.time)}
                  </p>
                ) : null}

                <h2>{event.title}</h2>
                <p className="event-location">{event.location}</p>
              </div>

              <div className="event-actions">
                {event.ticketsUrl ? (
                  <a
                    className="event-link"
                    href={event.ticketsUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    {event.isFree ? "Free Entry" : "Get Tickets"}
                  </a>
                ) : (
                  <span className="event-link disabled-link">
                    {event.isFree ? "Free Entry" : "Ticket link coming soon"}
                  </span>
                )}
                {showAdminControls && (
                  <button
                    type="button"
                    className="delete-event-button"
                    onClick={() => handleDeleteEvent(event.id)}
                  >
                    Delete
                  </button>
                )}
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export const AdminEventManager = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!auth || !firebaseReady) {
      setLoading(false);
      return undefined;
    }

    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  if (!firebaseReady || !auth) {
    return (
      <section className="events-page">
        <div className="events-header">
          <p className="events-kicker">Admin access</p>
          <h1>Connect Firebase to enable the private event editor.</h1>
        </div>
      </section>
    );
  }

  if (loading) {
    return (
      <section className="events-page">
        <div className="events-header">
          <p className="events-kicker">Admin access</p>
          <h1>Checking your session...</h1>
        </div>
      </section>
    );
  }

  if (!user) {
    return <Navigate to="/admin-login" replace />;
  }

  return <Events adminMode />;
};

export default Events;
